import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Receipt, Stethoscope, User, LogOut, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { theme } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  PATIENT PORTAL (Phase 10) — read-only self-view.
//  Opened from WhatsApp magic link. No login — token is the credential.
//  Props: API_BASE, token (from URL)
// ═══════════════════════════════════════════════════════════════════

const PatientPortalPage = ({ API_BASE, token: propToken }) => {
  // Accept token from prop, URL path, or ?token=
  const token = propToken
    || (typeof window !== 'undefined' && window.location.pathname.match(/\/patient\/([a-f0-9]+)/)?.[1])
    || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('token'));

  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!token) { setErr('No token provided'); setLoading(false); return; }
    axios.get(`${API_BASE}/patient-portal/me?token=${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [API_BASE, token]);

  if (loading) {
    return <div style={centerWrap}><div style={{ color:'#666' }}>Loading your portal…</div></div>;
  }
  if (err) {
    return (
      <div style={centerWrap}>
        <AlertCircle size={48} color={theme.danger} />
        <h2 style={{ color: theme.danger, marginTop:12 }}>Access denied</h2>
        <p style={{ color:'#666', textAlign:'center', maxWidth:400 }}>{err}</p>
        <p style={{ color:'#888', fontSize:13, marginTop:20 }}>Please request a new link from the clinic.</p>
      </div>
    );
  }

  const { patient, appointments, treatments, bills, summary } = data;

  return (
    <div style={{ minHeight:'100vh', background:'#f5f7fa' }}>
      {/* Header */}
      <div style={{ background: theme.primary, color:'white', padding:'24px 20px' }}>
        <div style={{ maxWidth:900, margin:'0 auto', display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ width:56, height:56, borderRadius:28, background:'white', color: theme.primary, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:800 }}>
            {patient.name?.[0] || '?'}
          </div>
          <div>
            <h1 style={{ margin:0, fontSize:22, fontWeight:700 }}>Hi, {patient.name}</h1>
            <div style={{ opacity:0.85, fontSize:13, marginTop:4 }}>
              UHID: {patient.uhid || '—'} · {patient.phone}
            </div>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ maxWidth:900, margin:'-20px auto 0', padding:'0 20px', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
        <KPI icon={Calendar} label="Appointments" value={appointments.length} color={theme.secondary}/>
        <KPI icon={Stethoscope} label="Treatments" value={summary.totalTreatments} color={theme.info}/>
        <KPI icon={Receipt} label="Total Billed" value={`₹${summary.totalBilled.toLocaleString('en-IN')}`} color={theme.warning}/>
        <KPI icon={CheckCircle} label="Paid" value={`₹${summary.totalPaid.toLocaleString('en-IN')}`} color={theme.success}/>
      </div>

      {/* Tabs */}
      <div style={{ maxWidth:900, margin:'20px auto 0', padding:'0 20px' }}>
        <div style={{ display:'flex', gap:8, borderBottom:`1px solid ${theme.border}`, marginBottom:16, flexWrap:'wrap' }}>
          {[
            { k:'overview', label:'📋 Overview' },
            { k:'appointments', label:'📅 Appointments' },
            { k:'treatments', label:'🦷 Treatments' },
            { k:'bills', label:'💰 Bills' },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              style={{
                padding:'10px 16px', border:'none', cursor:'pointer',
                borderBottom: tab===t.k ? `3px solid ${theme.primary}` : '3px solid transparent',
                background:'transparent', color: tab===t.k ? theme.primary : '#666',
                fontWeight: tab===t.k ? 700 : 500,
              }}>{t.label}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <div style={card}>
            <h3 style={{ margin:'0 0 12px', color: theme.navy }}>Your Profile</h3>
            <Row label="Name" value={patient.name}/>
            <Row label="UHID" value={patient.uhid || '—'}/>
            <Row label="Phone" value={patient.phone}/>
            <Row label="Email" value={patient.email || '—'}/>
            <Row label="Gender" value={patient.gender || '—'}/>
            <Row label="Age" value={patient.age || '—'}/>
            <Row label="Address" value={patient.address || '—'}/>
            {summary.plannedTreatments > 0 && (
              <div style={{ marginTop:16, padding:12, background:'#fef3c7', borderRadius:8, display:'flex', gap:10, alignItems:'center' }}>
                <Clock size={20} color="#92400e"/>
                <span style={{ color:'#92400e', fontSize:14 }}>
                  You have <b>{summary.plannedTreatments}</b> planned treatment{summary.plannedTreatments > 1 ? 's' : ''} pending. Book an appointment to proceed.
                </span>
              </div>
            )}
          </div>
        )}

        {tab === 'appointments' && (
          <div style={card}>
            {appointments.length === 0 ? <Empty text="No appointments yet"/>
             : appointments.map(a => (
              <div key={a._id} style={row}>
                <div>
                  <div style={{ fontWeight:600, color: theme.navy }}>{new Date(a.date).toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short', year:'numeric' })} · {a.time}</div>
                  <div style={{ fontSize:13, color:'#666' }}>{a.treatmentType || a.reason || '—'} · {a.doctorName || 'Any doctor'}</div>
                </div>
                <StatusPill status={a.status}/>
              </div>
            ))}
          </div>
        )}

        {tab === 'treatments' && (
          <div style={card}>
            {treatments.length === 0 ? <Empty text="No treatments yet"/>
             : treatments.map(t => (
              <div key={t._id} style={row}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, color: theme.navy }}>
                    {t.treatmentName}{t.toothNumber ? ` · Tooth #${t.toothNumber}` : ''}
                  </div>
                  <div style={{ fontSize:13, color:'#666' }}>
                    {t.status === 'done' ? `Done on ${new Date(t.doneDate).toLocaleDateString('en-IN')}` : `Planned for ${new Date(t.plannedDate).toLocaleDateString('en-IN')}`}
                    {' · ₹'}{t.totalAmount || 0}
                  </div>
                  {t.notes && <div style={{ fontSize:12, color:'#888', fontStyle:'italic', marginTop:4 }}>{t.notes}</div>}
                </div>
                <StatusPill status={t.status}/>
              </div>
            ))}
          </div>
        )}

        {tab === 'bills' && (
          <div style={card}>
            {bills.length === 0 ? <Empty text="No bills yet"/>
             : bills.map(b => {
              const due = (b.totalAmount || 0) - (b.amountPaid || 0);
              return (
                <div key={b._id} style={row}>
                  <div>
                    <div style={{ fontWeight:600, color: theme.navy }}>{b.billNumber || b._id.slice(-6).toUpperCase()}</div>
                    <div style={{ fontSize:13, color:'#666' }}>{new Date(b.createdAt).toLocaleDateString('en-IN')} · {b.billType || 'service'}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontWeight:700, color: theme.navy }}>₹{(b.totalAmount||0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize:12, color: due > 0 ? theme.danger : theme.success, fontWeight:600 }}>
                      {due > 0 ? `Due: ₹${due.toLocaleString('en-IN')}` : '✓ Paid'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ textAlign:'center', padding:20, color:'#888', fontSize:12 }}>
        This portal is read-only. To book or reschedule, please call the clinic.
      </div>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, color }) => (
  <div style={{ background:'white', padding:'14px', borderRadius:10, display:'flex', gap:10, alignItems:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
    <div style={{ width:36, height:36, borderRadius:8, background: color + '22', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Icon size={18} color={color}/>
    </div>
    <div>
      <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:700, color: theme.navy }}>{value}</div>
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div style={{ display:'flex', padding:'8px 0', borderBottom:'1px solid #f1f3f5' }}>
    <div style={{ width:120, color:'#888', fontSize:13 }}>{label}</div>
    <div style={{ color: theme.navy, fontSize:14 }}>{value}</div>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    done: { bg:'#dcfce7', fg:'#166534' }, planned: { bg:'#dbeafe', fg:'#1e40af' },
    booked: { bg:'#dbeafe', fg:'#1e40af' }, confirmed: { bg:'#dcfce7', fg:'#166534' },
    completed: { bg:'#dcfce7', fg:'#166534' }, cancelled: { bg:'#fee2e2', fg:'#991b1b' },
    'no-show': { bg:'#fee2e2', fg:'#991b1b' },
  };
  const c = map[status] || { bg:'#f3f4f6', fg:'#374151' };
  return <span style={{ padding:'3px 10px', borderRadius:12, fontSize:11, fontWeight:700, textTransform:'uppercase', background: c.bg, color: c.fg }}>{status}</span>;
};

const Empty = ({ text }) => <div style={{ padding:30, textAlign:'center', color:'#888' }}>{text}</div>;

const card = { background:'white', padding:'16px', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)' };
const row = { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid #f1f3f5', gap:10 };
const centerWrap = { minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:20, background:'#f5f7fa' };

export default PatientPortalPage;
